import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { CoverPage } from '../models';

interface CoverPageResponse {
  id: number;
  year: number;
  month: number;
  fileName: string;
  filePath: string;
  fileUrl: string;
  fileSize: number;
  contentType: string;
  createdAt: string;
  updatedAt: string;
}

@Injectable({
  providedIn: 'root'
})
export class CoverPagesService {
  private apiUrl = '/api/coverpages';

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  private getHeaders(): HttpHeaders {
    return this.authService.getAuthHeaders();
  }

  getAllCoverPages(): Observable<CoverPage[]> {
    return this.http.get<CoverPageResponse[]>(this.apiUrl, {
      headers: this.getHeaders()
    }).pipe(
      map(coverPages => coverPages.map(cp => this.mapToCoverPage(cp)))
    );
  }

  getCoverPageById(id: number): Observable<CoverPage> {
    return this.http.get<CoverPageResponse>(`${this.apiUrl}/${id}`, {
      headers: this.getHeaders()
    }).pipe(
      map(coverPage => this.mapToCoverPage(coverPage))
    );
  }

  getCoverPageByDate(year: number, month: number): Observable<CoverPage | null> {
    return this.http.get<CoverPageResponse>(`${this.apiUrl}/by-date/${year}/${month}`).pipe(
      map(coverPage => this.mapToCoverPage(coverPage))
    );
  }

  createCoverPage(request: { year: number; month: number; file: File }): Observable<CoverPage> {
    const formData = new FormData();
    formData.append('year', request.year.toString());
    formData.append('month', request.month.toString());
    formData.append('file', request.file);

    return this.http.post<CoverPageResponse>(this.apiUrl, formData).pipe(
      map(coverPage => this.mapToCoverPage(coverPage))
    );
  }

  updateCoverPage(id: number, request: { year: number; month: number; file?: File }): Observable<CoverPage> {
    const formData = new FormData();
    formData.append('year', request.year.toString());
    formData.append('month', request.month.toString());
    if (request.file) {
      formData.append('file', request.file);
    }

    return this.http.put<CoverPageResponse>(`${this.apiUrl}/${id}`, formData).pipe(
      map(coverPage => this.mapToCoverPage(coverPage))
    );
  }

  deleteCoverPage(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`, {
      headers: this.getHeaders()
    });
  }

  private mapToCoverPage(response: CoverPageResponse): CoverPage {
    return {
      id: response.id,
      year: response.year,
      month: response.month,
      fileName: response.fileName,
      filePath: response.filePath,
      fileUrl: response.fileUrl,
      fileSize: response.fileSize,
      contentType: response.contentType,
      createdAt: new Date(response.createdAt)
    };
  }
}
